import { useI18n } from '~/common/context';
/* eslint-disable @wyz/module/no-relative-import */
import { RecordKeys } from '~/common/types';
import { CenteredSpinner } from '@wyz/ui';
import { mapIntoOptionItem } from '@wyz/utils';
import React, { useMemo } from 'react';
import { useManageEnums } from '~/common/api';
import { PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS } from '~/common/translators';
import {
  ISupplierTypeOptionItem,
  IVehicleTypeGroupsOptionItem,
} from '~/common/types';
import { FormContext, IFormContext } from './contexts';
import { TYRE_COUNT_ENUM } from '~/purchase/schemas';
import { ITyreCountType } from '~/purchase/types';
import { useActiveVehicleTypeGroups } from '~/purchase/hooks';

export function RedistributionServicePricingPolicyFormProvider({
  children,
  mode,
}: React.PropsWithChildren<Pick<IFormContext, 'mode'>>) {
  const { t } = useI18n();
  const manageEnums = useManageEnums({
    getSupplierTypes: { enabled: true, args: { scope: 'PRICING_POLICY' } },
  });

  const activeVehicleTypeGroups = useActiveVehicleTypeGroups();
  const context: IFormContext = useMemo(() => {
    return {
      mode,
      vehicleTypeGroups: {
        ...activeVehicleTypeGroups,
        optionItems: mapIntoOptionItem(
          activeVehicleTypeGroups.data?.data ?? [],
          (item) => t(item.i18nKey, { defaultValue: item.name }),
          'publicKey',
          (item) => ({ name: item.name }),
        ) as Array<IVehicleTypeGroupsOptionItem>,
      },
      tyreCounts: {
        optionItems: Object.keys(TYRE_COUNT_ENUM).map((tyreCountCodeName) => ({
          value: tyreCountCodeName as ITyreCountType,
          label: t('app.quote.tyre_count', {
            count:
              TYRE_COUNT_ENUM[
                tyreCountCodeName as RecordKeys<typeof TYRE_COUNT_ENUM>
              ],
          }),
        })),
      },
      supplierTypes: {
        ...manageEnums.getSupplierTypes,
        optionItems: mapIntoOptionItem(
          manageEnums.getSupplierTypes.query.data?.data ?? [],
          (item) => t(PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS[item.value]),
          'codename',
          (item) => ({ id: item.value }),
        ) as Array<ISupplierTypeOptionItem>,
      },
    } satisfies IFormContext;
  }, [manageEnums.getSupplierTypes, activeVehicleTypeGroups, mode, t]);

  const requiredQueries = [activeVehicleTypeGroups];
  return (
    <FormContext.Provider value={context}>
      {requiredQueries.some((query) => !query.isSuccess) && <CenteredSpinner />}
      {requiredQueries.every((query) => query.isSuccess) && children}
    </FormContext.Provider>
  );
}
export const useRedistributionServicePricingPolicyForm = () => {
  const context = React.useContext(FormContext);
  if (context === undefined) {
    throw new Error(
      'useRedistributionServicePricingPolicyForm must be used within a RedistributionServicePricingPolicyFormProvider',
    );
  }
  return context;
};
